import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { AlertTriangle, ShieldCheck, CheckCircle, XCircle, RefreshCw, Clock } from 'lucide-react';
import NotifyIcon from '../../components/common/NotifyIcon';
import { apiService } from '../../services/api.service';
import { API_ENDPOINTS } from '../../config/environment';

const STATUS_MAP = {
    1: { label: 'Aktif', color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
    2: { label: 'Onaylandi', color: '#3b82f6', bg: 'rgba(59,130,246,0.12)' },
    3: { label: 'Islemde', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
    4: { label: 'Cozuldu', color: '#10b981', bg: 'rgba(16,185,129,0.12)' },
    5: { label: 'Yanlis Alarm', color: '#6b7280', bg: 'rgba(107,114,128,0.12)' },
};

const formatDate = (d) => d ? new Date(d).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

const FleetEmergencySection = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [respondingId, setRespondingId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const res = await apiService.post(API_ENDPOINTS.EMERGENCY.LOGS, {
            page: 1, pageSize: 50,
            sortModels: [{ colId: 'createdAtUtc', sort: 'desc' }],
            filterModel: null
        });
        // Zarf tek katman: `res.data` doğrudan sayfalı sonuç. `res.data.success` aranırken
        // koşul hiç tutmuyor, acil durum kaydı olsa bile bölüm hiç çizilmiyordu.
        if (res.success) {
            const all = (res.data?.items || []).map(e => ({
                ...e,
                status: e.status > 0 ? e.status : (e.resolvedAtUtc ? 4 : 1)
            }));
            setItems(all.filter(e => e.status === 1 || e.status === 2 || e.status === 3));
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        load();
        const interval = setInterval(load, 30000);
        return () => clearInterval(interval);
    }, [load]);

    const respond = async (logId, status) => {
        setRespondingId(logId);
        const res = await apiService.put(API_ENDPOINTS.EMERGENCY.FLEET_RESPOND(logId), { status, note: null });
        setRespondingId(null);
        if (res.success) {
            toast.success(`Acil durum ${STATUS_MAP[status]?.label?.toLowerCase() || 'guncellendi'}`);
            load();
        } else {
            toast.error(res.error || 'Islem basarisiz');
        }
    };

    // Acil durum yokken hiç render etme (loading sırasında da). Aksi halde boş yanıt gelen
    // her poll'de (30sn) bölüm kısa süre açılıp kapanıyor → sayfa yukarı/aşağı zıplıyordu.
    if (items.length === 0) return null;

    return (
        <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <AlertTriangle size={16} color="#ef4444" />
                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary, #09090b)' }}>
                    Aktif Acil Durumlar ({items.length})
                </span>
                <button onClick={load} disabled={loading} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted, #64748b)', padding: 4 }}>
                    <RefreshCw size={14} className={loading ? 'spinning' : ''} />
                </button>
            </div>

            {items.map(em => {
                const si = STATUS_MAP[em.status] || STATUS_MAP[1];
                return (
                    <div key={em.id} style={{
                        background: si.bg,
                        border: `1px solid ${si.color}33`,
                        borderRadius: 10,
                        padding: '12px 16px',
                        marginBottom: 8,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        flexWrap: 'wrap',
                    }}>
                        <div style={{ flex: 1, minWidth: 180 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                                <NotifyIcon name="siren" size={16} />
                                <strong style={{ fontSize: 13, color: si.color }}>{em.courierFullName}</strong>
                                <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: si.bg, color: si.color, fontWeight: 600, border: `1px solid ${si.color}44` }}>
                                    {si.label}
                                </span>
                            </div>
                            <div style={{ fontSize: 12, color: 'var(--text-secondary, #94a3b8)' }}>
                                {em.reason || 'Sebep belirtilmedi'}
                            </div>
                            <div style={{ fontSize: 11, color: 'var(--text-muted, #64748b)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                                <Clock size={10} /> {formatDate(em.createdAtUtc)}, {em.reassignedDeliveryCount} siparis
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                            {em.status === 1 && (
                                <button onClick={() => respond(em.id, 2)} disabled={respondingId === em.id}
                                    style={{ padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', background: 'rgba(59,130,246,0.15)', color: '#60a5fa', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4, opacity: respondingId === em.id ? 0.5 : 1 }}>
                                    <ShieldCheck size={12} /> Onayla
                                </button>
                            )}
                            <button onClick={() => respond(em.id, 4)} disabled={respondingId === em.id}
                                style={{ padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', background: 'rgba(16,185,129,0.15)', color: '#34d399', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4, opacity: respondingId === em.id ? 0.5 : 1 }}>
                                <CheckCircle size={12} /> Coz
                            </button>
                            <button onClick={() => respond(em.id, 5)} disabled={respondingId === em.id}
                                style={{ padding: '5px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', background: 'rgba(107,114,128,0.15)', color: '#9ca3af', fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4, opacity: respondingId === em.id ? 0.5 : 1 }}>
                                <XCircle size={12} /> Yanlis Alarm
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default FleetEmergencySection;
